/**
 * Client-side image compression before upload — keeps GCS storage + egress
 * costs down in production (direct-to-GCS PUT bypasses the server).
 *
 * Skips SVG/GIF and files already under ~400 KB. Videos are unchanged.
 */

const SKIP_TYPES = new Set(['image/svg+xml', 'image/gif']);

/**
 * @param {File} file
 * @param {{ maxWidth?: number, quality?: number, minBytes?: number }} [opts]
 * @returns {Promise<File>}
 */
export async function compressImageFileForUpload(file, opts = {}) {
  const maxWidth = opts.maxWidth ?? 1920;
  const quality = opts.quality ?? 0.82;
  const minBytes = opts.minBytes ?? 400 * 1024;
  const type = String(file?.type || '').toLowerCase();
  if (!type.startsWith('image/') || SKIP_TYPES.has(type)) return file;
  if (file.size <= minBytes) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const scale = bitmap.width > maxWidth ? maxWidth / bitmap.width : 1;
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close?.();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/webp', quality);
    });
    if (!blob || blob.size >= file.size) return file;
    const base = (file.name || 'upload').replace(/\.[^.]+$/, '') || 'upload';
    return new File([blob], `${base}.webp`, { type: 'image/webp', lastModified: Date.now() });
  } catch {
    return file;
  }
}

/**
 * @param {File} file
 * @param {'badge'|'event'|string} kind
 */
export async function prepareUploadFile(file, kind = 'event') {
  const type = String(file?.type || '').toLowerCase();
  if (type.startsWith('video/')) return file;
  const maxWidth = kind === 'avatar' ? 400 : kind === 'badge' ? 512 : 1920;
  return compressImageFileForUpload(file, { maxWidth, quality: kind === 'badge' ? 0.88 : 0.82 });
}
